/**
 * Suggested filename for a batch export.
 *   `receipts-YYYY-MM-DD.<extension>`
 * Today's date — users typically download "what I just exported", not a
 * filename pegged to any specific receipt's date.
 */
export function batchFilename(extension: string): string {
  const today = new Date().toISOString().slice(0, 10);
  return `receipts-${today}.${extension}`;
}

/**
 * English 3-letter month abbreviations, indexed by `getMonth()`.
 *
 * Deliberately NOT localised. These land in filenames and Excel sheet
 * names, where `Août` / `Dez` would either be mangled by the sanitiser
 * below (accents get decomposed and stripped) or vary per user, making
 * the same receipt export under different names depending on the
 * interface language. A fixed English set keeps exports stable and
 * portable across filesystems.
 */
const MONTH_ABBR = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

/**
 * Build a per-receipt filename `vendor_Mon_YY` (e.g. `Mercadona_Aug_26`)
 * from a parsed receipt. Vendor-first so exports group by provider, which
 * is how people filing receipts for bookkeeping tend to look for them.
 *
 * Trade-off vs the previous `YYYY-MM-DD_vendor`: files no longer sort
 * chronologically in a file manager (alphabetical on `Mon` puts Apr before
 * Jan), and month-granularity means two receipts from the same vendor in
 * the same month collide — `uniqueStem` disambiguates those with `_2`,
 * `_3`, … so nothing is lost, there are just more suffixes than before.
 *
 * LOCAL-calendar based, matching the review UI (`date-format.ts`): the
 * parser creates receipt dates at local midnight, so UTC getters would
 * shift the month back a day's worth for users east of Greenwich and
 * name the file differently from the date shown on screen.
 *
 * Sanitisation rules for the vendor portion:
 *   - non-alphanumerics (other than `-`) → `_`
 *   - consecutive underscores collapsed
 *   - leading/trailing underscores stripped
 *   - vendor truncated to 50 chars to leave room for the month + year +
 *     extension while staying under common filesystem limits
 *
 * Returns the stem only (no extension) — the caller appends `.jpg` etc.
 * The caller is also responsible for collision-suffixing when two receipts
 * produce the same stem; see `uniqueStem`.
 */
export function receiptFilenameStem(vendor: string, date: Date): string {
  const slug = vendor
    .normalize('NFKD')
    .replace(/[^\p{L}\p{N}-]+/gu, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 50);
  const safeVendor = slug.length === 0 ? 'receipt' : slug;
  const month = MONTH_ABBR[date.getMonth()] ?? 'Jan';
  const year = String(date.getFullYear() % 100).padStart(2, '0');
  return `${safeVendor}_${month}_${year}`;
}

/**
 * Append `_2`, `_3`, ... to a filename stem when a previous receipt already
 * claimed the same one in this batch. Mutates the provided `seen` map so
 * callers can call repeatedly across a batch.
 *
 * Every returned name is registered in `seen` too — including generated
 * suffixed ones — so a NATURAL stem like `Foo_2` can never collide with a
 * generated `Foo_2` (in either arrival order).
 */
export function uniqueStem(stem: string, seen: Map<string, number>): string {
  const count = seen.get(stem);
  if (count === undefined) {
    seen.set(stem, 1);
    return stem;
  }
  let n = count + 1;
  let candidate = `${stem}_${n}`;
  while (seen.has(candidate)) {
    n += 1;
    candidate = `${stem}_${n}`;
  }
  seen.set(stem, n);
  seen.set(candidate, 1);
  return candidate;
}

/**
 * Unique Excel sheet name from a filename stem. Excel caps sheet names
 * at 31 chars, so truncate FIRST — leaving room for a `_2`..`_99` dedupe
 * suffix — then dedupe. Truncating after deduping would cut the suffix
 * back off for 31-char stems, re-creating the duplicate and making
 * SheetJS's `book_append_sheet` throw.
 */
export function uniqueSheetName(stem: string, seen: Map<string, number>): string {
  return uniqueStem(stem.slice(0, 28), seen);
}
